import { XMarkIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { stringify } from 'query-string';
import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router';

import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import { useAdminPagePrompts } from '@dashboard/hooks/queries/useAdminQueries';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import Label from '@dashboard/components/Form/components/Label';
import TextInput from '@dashboard/components/Form/components/TextInput';
import Link from '@dashboard/components/Link';
import { Badge } from '@dashboard/components/ui/badge';
import Tooltip from '@dashboard/components/Tooltip';

const PagePrompts = () => {
  const history = useHistory();

  const query = useUrlQuery();
  const { search, searchInput, setSearchInput } = useSearchInput(query.search);
  const [sortBy, setSortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || -1);
  const [promptType, setPromptType] = useState(query.promptType || '');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        search,
        ...(promptType && { promptType }),
      }),
    });
  }, [sortBy, sortDirection, search, promptType]);

  const filters = {
    sortBy,
    sortDirection,
    search,
    promptType,
    limit: 20,
  };

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    useAdminPagePrompts(filters);

  // Convert sortBy/sortDirection to TanStack SortingState
  const sorting = useMemo(
    () => [
      {
        id: sortBy,
        desc: sortDirection === -1,
      },
    ],
    [sortBy, sortDirection],
  );

  // Handle sorting changes from DataGrid
  const handleSortingChange = updater => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      setSortBy(id);
      setSortDirection(desc ? -1 : 1);
    }
  };

  // Flatten pages data for DataGrid
  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  // Define columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'prompt',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Prompt" />,
        cell: ({ row }) => (
          <Tooltip
            label={row.getValue('prompt')}
            side="bottom"
            align="start"
            triggerClassName="w-full text-left align-middle"
          >
            <div className="truncate cursor-help max-w-md">{row.getValue('prompt')}</div>
          </Tooltip>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'type',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Type" />,
        cell: ({ row }) => (
          <Badge
            variant={row.getValue('type') === 'initial' ? 'success' : 'secondary'}
            className="first-letter:uppercase"
          >
            {row.getValue('type')}
          </Badge>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'page.name',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Page" />,
        cell: ({ row }) => {
          const page = row.original.page;
          if (!page) {
            return <span className="text-zinc-400 dark:text-muted-foreground">Page deleted</span>;
          }
          return (
            <Link href={`/pages/${page._id}/customize`}>
              {page.name || page.slug || 'Untitled'}
            </Link>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'seller.displayName',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Merchant" />,
        cell: ({ row }) => {
          const seller = row.original.seller;
          if (!seller) {
            return (
              <span className="text-zinc-400 dark:text-muted-foreground">Merchant deleted</span>
            );
          }
          return (
            <Link href={`/admin/merchants/${seller._id}`}>{seller.displayName || seller.name}</Link>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Date" />,
        cell: ({ row }) => {
          const createdAt = row.getValue('createdAt');
          return createdAt ? format(new Date(createdAt), 'dd MMM yyyy HH:mm') : '';
        },
        enableSorting: true,
      },
    ],
    [],
  );

  const total = data?.pages[0]?.total;

  // Filter controls for custom toolbar actions
  const filterControls = (
    <div className="flex items-end gap-x-2">
      <div className="mr-auto text-sm text-gray-500 dark:text-muted-foreground">
        {total} prompts
      </div>

      <div>
        <Label htmlFor="promptType">Prompt type</Label>
        <Select
          name="promptType"
          value={promptType || 'all'}
          onValueChange={value => setPromptType(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="initial">Initial</SelectItem>
            <SelectItem value="iteration">Iteration</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="search">Search</Label>
        <div className="w-64">
          <div className="relative flex items-center">
            <TextInput
              border
              name="search"
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search by prompt, page, or merchant"
              value={searchInput}
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="flex items-center absolute right-0 top-0 bottom-0 p-1.5 m-px bg-white dark:bg-background text-gray-300 dark:text-muted-foreground hover:text-gray-400 dark:hover:text-muted-foreground transition-colors rounded"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pr-4">
      <div className="flex items-end justify-between mb-4">
        <h1>Page prompts</h1>
        {filterControls}
      </div>

      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No page prompts found</EmptyState.Title>
          </EmptyState>
        }
      />
    </div>
  );
};

export default PagePrompts;
